import { useMemo, useCallback } from 'react';
import { getListingImages } from '../../../util/listing';

/**
 * Helper to extract image URL from image object
 */
const getImageUrl = image => {
  const variants = image?.attributes?.variants;
  return variants?.['scaled-large']?.url || variants?.default?.url;
};

/**
 * Custom hook to get package preview images with fallback to listing images
 * @param {Array} packageImagesIds - Array of package image IDs
 * @param {Object} listing - The current listing object
 * @returns {Array} - Array of image URLs
 */
const usePackagePreviewImages = (packageImagesIds, listing) => {
  const getPackageImages = useCallback(
    imageIds => {
      return (imageIds || [])
        .map(imageId => {
          const image = listing?.images?.find(img => img.id.uuid === imageId);
          return getImageUrl(image);
        })
        .filter(Boolean);
    },
    [listing?.images]
  );

  const images = useMemo(() => {
    // First try package images
    const packageImages = getPackageImages(packageImagesIds);

    if (packageImages.length > 0) {
      return packageImages;
    }

    // Fallback to listing images (filtered by 'property' tag)
    const listingImages = getListingImages(listing);
    return listingImages.map(getImageUrl).filter(Boolean);
  }, [packageImagesIds, getPackageImages, listing]);

  return images;
};

export default usePackagePreviewImages;
